import {
  Collapse,
  Icon,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled,
  Theme,
  TIcons,
  Tooltip,
  useTheme,
} from '@yuno/design-system-web'
import { createBrowserHistory } from 'history'
import { useEffect, useState } from 'react'
import { Submenues } from './drawer-item'

type DrawerItemProps = {
  text: string
  icon: TIcons
  onClick?: () => void
  selected?: boolean
  disableTooltip?: boolean
  disabled?: boolean
  submenues: Submenues[]
}

const TEXT_STYLES = ({ text, theme, colorSelected, override = {} }) => ({
  marginLeft: '5px',
  color: text !== 'NONE' ? colorSelected : theme.palette.neutral.white[0],
  ...override,
})

const ListItemStyled = styled(ListItem)(({ theme }: { theme?: Theme }) => ({
  '& .MuiListItemButton-root.Mui-selected': {
    background: theme.palette.grey[1],
  },
}))

export const DrawerSubmenu = ({
  text,
  icon,
  selected,
  disableTooltip,
  disabled,
  submenues,
}: DrawerItemProps) => {
  const history = createBrowserHistory()
  const [open, setOpen] = useState(false)
  const [opened, setOpened] = useState(false)

  const theme: Theme = useTheme()
  const colorSelected = theme.palette.grey.main

  useEffect(() => {
    if (
      submenues.find((el) => el.link === history.location.pathname) &&
      !opened
    ) {
      setOpen(true)
      setOpened(true)
    }
  }, [history, open])

  const handleMenuOpen = () => {
    setOpen((prev) => !prev)
  }

  const handleParentClick = (submenues) => {
    const primaryLink = submenues?.[0]?.link

    if (primaryLink !== location.pathname) {
      window.singleSpaNavigate(primaryLink)
    }

    if (open) {
      setOpen(false)
      return
    }

    setOpen(true)
  }

  const handleClick = (link) => {
    if (link !== location.pathname) {
      window.singleSpaNavigate(link)
    }
  }

  return (
    <>
      <Tooltip
        title={disabled ? 'You do not have permission for this feature.' : text}
        color={disabled ? 'default' : 'primary'}
        placement={disabled ? 'bottom' : 'right'}
        arrow
        disableHoverListener={disableTooltip && !disabled}
      >
        <ListItemStyled
          disablePadding
          onClick={handleParentClick.bind(null, submenues)}
        >
          <ListItemButton
            selected={selected}
            sx={{ paddingLeft: '10px' }}
            disabled={disabled}
          >
            <ListItemIcon sx={{ justifyContent: 'center' }}>
              <Icon name={icon} size="16px" color={colorSelected} />
            </ListItemIcon>
            <ListItemText
              sx={TEXT_STYLES({ text, theme, colorSelected })}
              primary={text}
            />
            <IconButton disableRipple onClick={handleMenuOpen} size="small">
              <Icon
                name={!open ? 'CaretDown' : 'CaretUp'}
                size="12px"
                color={theme.palette.neutral.black}
              />
            </IconButton>
          </ListItemButton>
        </ListItemStyled>
      </Tooltip>
      <Collapse in={open} timeout="auto" unmountOnExit>
        {submenues.map((el) => (
          <ListItemStyled
            disablePadding
            key={el.name}
            sx={
              location.pathname === el.link
                ? {
                    borderLeft: `2px solid ${theme.palette.vivid.brand}`,
                    background: theme.palette.grey[1],
                  }
                : {}
            }
          >
            <ListItemButton
              sx={{ paddingLeft: 0 }}
              onClick={handleClick.bind(null, el.link)}
            >
              <ListItemText
                primary={`${el.name}`}
                sx={TEXT_STYLES({
                  text: el.name,
                  theme,
                  colorSelected:
                    location.pathname === el.link
                      ? theme.palette.vivid.brand
                      : colorSelected,
                  override: {
                    marginLeft: 0,
                    paddingLeft: '72px',
                  },
                })}
              />
            </ListItemButton>
          </ListItemStyled>
        ))}
      </Collapse>
    </>
  )
}
